import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import { ThemeProvider } from '@/lib/theme';
// Inter was declared in index.css (plus four Inter-only `cv0x` character
// variants) but never actually loaded — no @font-face, no CDN link, no
// package. Every user was reading Segoe UI / SF while the type scale had been
// tuned against Inter's metrics. Self-hosted variable font: one woff2, no
// third-party request, no layout shift from a late swap.
import '@fontsource-variable/inter';
import './index.css';

// ── Force everyone onto the latest deploy ───────────────────────────────────
// registerType:'autoUpdate' + skipWaiting + clientsClaim (vite.config) make a
// new service worker take over immediately AND reload the page once it
// activates. The missing piece was update *detection*: a long-lived session
// (installed PWA, always-open tab) only checks for a new version on a fresh
// load, so it could run stale JS for days. Here we poll for updates every
// 60s and whenever the tab regains focus, so a new deploy rolls out to
// everyone within ~1 minute with no manual hard-refresh.
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    setInterval(() => {
      registration.update().catch(() => {});
    }, 60_000);
    window.addEventListener('focus', () => {
      registration.update().catch(() => {});
    });
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
